import { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../../config";

export default function StudentValidations() {
  const [students, setStudents] = useState([]);
  const [index, setIndex] = useState(0);
  const [comment, setComment] = useState("");
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadStudents = async () => {
    const res = await axios.get(`${base_url}/api/admin/student_validations`);
    setStudents(res.data);
    setIndex(0);
    setComment("");
    setFileUrl(null);
    setFileType(null);
  };

  useEffect(() => {
    loadStudents();
  }, []);

  const current = students[index] ?? null;

  useEffect(() => {
    if (!current) return;
    setFileUrl(null);
    setFileType(null);
    setComment("");
    setError("");

    axios
      .get(`${base_url}/api/admin/student_validations/${current.id}/file`, {
        responseType: "blob",
      })
      .then((res) => {
        const name = (current.student_proof_file_name ?? "").toLowerCase();
        const isPdf = name.endsWith(".pdf");
        const mimeType = isPdf ? "application/pdf" : res.data.type;
        const blob = new Blob([res.data], { type: mimeType });
        const url = URL.createObjectURL(blob);
        setFileUrl(url);
        setFileType(isPdf ? "pdf" : "image");
      })
      .catch(() => {
        setFileUrl(null);
        setFileType(null);
      });
  }, [current?.id]);

  const handleDecision = async (decision) => {
    if (decision === "reject" && !comment.trim()) {
      setError("A comment is required when rejecting.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(
        `${base_url}/api/admin/student_validations/${current.id}/${decision}`,
        { comment: comment.trim() }
      );
      // Remove the decided student from the list
      const next = students.filter((_, i) => i !== index);
      setStudents(next);
      setIndex(Math.min(index, next.length - 1));
      setComment("");
      setFileUrl(null);
      setFileType(null);
    } catch (e) {
      setError("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (students.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <p className="text-xl font-semibold">No pending student validations</p>
        <p className="text-sm mt-2">All student proofs have been reviewed.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full">

      {/* Header zone */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 flex flex-wrap gap-6 items-center justify-between">
        <div className="flex flex-wrap gap-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-400">Name</p>
            <p className="text-base font-semibold text-gray-900 mt-0.5">{current.name}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-400">Email</p>
            <p className="text-base text-gray-900 mt-0.5">{current.email}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-400">Affiliation</p>
            <p className="text-base text-gray-900 mt-0.5">{current.affiliation}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-400">Registration ID</p>
            <p className="text-base font-mono font-bold text-blue-800 mt-0.5">{current.registration_id}</p>
          </div>
        </div>
        {/* Navigation */}
        <div className="flex items-center gap-3">
          <button
            className="button text-sm"
            disabled={index === 0}
            onClick={() => setIndex(index - 1)}
          >
            ← Previous
          </button>
          <span className="text-sm text-gray-500 font-medium">
            {index + 1} / {students.length}
          </span>
          <button
            className="button text-sm"
            disabled={index === students.length - 1}
            onClick={() => setIndex(index + 1)}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Canvas zone */}
      <div className="border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden" style={{ minHeight: "480px" }}>
        {fileUrl === null ? (
          <p className="text-gray-400 text-sm">Loading file…</p>
        ) : fileType === "pdf" ? (
          <iframe
            src={fileUrl}
            title="Student proof"
            className="w-full h-full rounded-lg"
            style={{ minHeight: "480px", border: "none" }}
          />
        ) : (
          <img
            src={fileUrl}
            alt="Student proof"
            className="max-h-full max-w-full object-contain rounded"
            style={{ maxHeight: "480px" }}
          />
        )}
      </div>

      {/* Decision zone */}
      <div className="border border-gray-200 rounded-lg p-5 bg-white flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Comment <span className="text-gray-400">(required for rejection)</span>
          </label>
          <textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment for the student…"
            className="w-full border border-gray-300 rounded-lg p-2.5 text-sm text-gray-900 bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-4">
          <button
            className="button text-sm bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg"
            disabled={submitting}
            onClick={() => handleDecision("approve")}
          >
            Approve
          </button>
          <button
            className="button text-sm bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
            disabled={submitting}
            onClick={() => handleDecision("reject")}
          >
            Reject
          </button>
        </div>
      </div>

    </div>
  );
}
